import { useCallback, useEffect, useState } from 'react';
import { formatSession } from '../format';
import { adminApi, errorText, type Question, type Registration, type ScheduleRule, type WebinarDetail, type WebinarStats } from './api';
import { navigate } from './app';
import { uploadText } from './upload-texts';
import { CardShell, useCollapsed } from './card';
import { ImageUpload } from './image-upload';
import { MailTemplates } from './mails';
import { VideoUpload } from './video-upload';

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Mo' }, { value: 2, label: 'Di' }, { value: 3, label: 'Mi' }, { value: 4, label: 'Do' }, { value: 5, label: 'Fr' }, { value: 6, label: 'Sa' }, { value: 7, label: 'So' },
];

const VIDEO_STATUS: Record<string, string> = { queued: 'wartet', probing: 'wird geprüft', transcoding: 'wird aufbereitet', ready: 'fertig', rejected: 'abgelehnt', failed: 'fehlgeschlagen' };

const toMinutes = (seconds: number | null) => (seconds === null ? '' : String(Math.round(seconds / 60)));
const fromMinutes = (value: string): number | null => (value.trim() === '' ? null : Math.max(0, Math.round(Number(value))) * 60);
const toClock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
const fromClock = (value: string): number => {
  const [m, s] = value.split(':').map((v) => Number(v));
  return Math.max(0, (Number.isFinite(m) ? m! : 0) * 60 + (Number.isFinite(s ?? 0) ? (s ?? 0) : 0));
};

export function WebinarEditPage({ id, projectSlug }: { id: string; projectSlug: string }) {
  const [webinar, setWebinar] = useState<WebinarDetail | null | undefined>(undefined);
  const reloadStable = useCallback(() => {
    adminApi
      .getWebinar(id)
      .then(setWebinar)
      .catch(() => setWebinar(null));
  }, [id]);
  const reload = reloadStable;
  useEffect(() => {
    reloadStable();
  }, [reloadStable]);

  if (webinar === undefined) return <p className="muted">Wird geladen …</p>;
  if (webinar === null) {
    return (
      <>
        <h1>Webinar nicht gefunden</h1>
        <p>
          <a href="/admin" onClick={(e) => (e.preventDefault(), navigate('/admin'))}>
            Zurück zur Übersicht
          </a>
        </p>
      </>
    );
  }

  return (
    <>
      <div className="admin-head">
        <div>
          <p className="label">
            <a href="/admin" onClick={(e) => (e.preventDefault(), navigate('/admin'))}>
              Webinare
            </a>{' '}
            › {webinar.title}
          </p>
          <h1>{webinar.title}</h1>
        </div>
        {webinar.status === 'active' && (
          <a className="button button--secondary" href={`${publicBase(projectSlug)}/w/${webinar.slug}`} target="_blank" rel="noopener">
            Anmeldeseite öffnen
          </a>
        )}
      </div>

      <StatsCard id={id} />

      <div className="admin-grid">
        <BasicsCard webinar={webinar} projectSlug={projectSlug} onSaved={setWebinar} />
        <VideoCard webinar={webinar} onChanged={reloadStable} />
        <WaitingImageCard webinar={webinar} onChanged={reloadStable} />
        <SchedulesCard webinar={webinar} onSaved={() => void reload()} />
        <HintsCard webinar={webinar} onSaved={() => void reload()} />
        <OfferCard webinar={webinar} onSaved={setWebinar} />
        <AfterCard webinar={webinar} onSaved={setWebinar} />
        <SettingsCard webinar={webinar} onSaved={setWebinar} />
      </div>

      <QuestionsCard id={id} />
      <RegistrationsCard id={id} count={webinar.registrations} />
      <CardShell id="webinar.mails" style={{ marginTop: 24 }} title="E-Mails dieses Webinars" defaultOpen={false}>
        <p className="muted small">Dieses Webinar nutzt die Vorlagen des Projekts (Reiter „E-Mails"). Änderst du hier eine Mail, gilt sie nur für dieses Webinar – die anderen bleiben wie im Projekt.</p>
        <MailTemplates webinarId={id} />
      </CardShell>
    </>
  );
}

function SaveBar({ busy, error, saved, label = 'Speichern' }: { busy: boolean; error: string | null; saved: boolean; label?: string }) {
  return (
    <div className="save-bar">
      <button type="submit" className="button" disabled={busy}>
        {busy ? 'Speichert …' : label}
      </button>
      {saved && <span className="muted small">Gespeichert.</span>}
      {error && <span className="error-inline">{error}</span>}
    </div>
  );
}

function useSave<T>(action: (input: T) => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const run = async (input: T) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await action(input);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, saved, run };
}

/** Teilnehmerseiten laufen unter <projekt>.<Plattform>; die Verwaltung unter app.<Plattform>. */
function publicBase(projectSlug: string): string {
  const host = window.location.host.replace(/^app\./, '');
  return `${window.location.protocol}//${projectSlug}.${host}`;
}

function BasicsCard({ webinar, projectSlug, onSaved }: { webinar: WebinarDetail; projectSlug: string; onSaved: (w: WebinarDetail) => void }) {
  const [title, setTitle] = useState(webinar.title);
  const [slug, setSlug] = useState(webinar.slug);
  const [status, setStatus] = useState(webinar.status);
  const save = useSave(async () => onSaved(await adminApi.updateWebinar(webinar.id, { title, slug, status })));

  return (
    <CardShell
      as="form"
      id="webinar.basics"
      title="Grunddaten"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run(undefined);
      }}
    >
      <div className="field">
        <label htmlFor="b-title">Titel</label>
        <input id="b-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required />
      </div>
      <div className="field">
        <label htmlFor="b-slug">Kurzname in der Adresse</label>
        <input id="b-slug" type="text" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} pattern="[a-z0-9][a-z0-9\-]{1,62}" required />
        <span className="faint small">Anmeldeseite: {publicBase(projectSlug)}/w/{slug}</span>
      </div>
      <div className="field">
        <label htmlFor="b-status">Status</label>
        <select id="b-status" value={status} onChange={(e) => setStatus(e.target.value as WebinarDetail['status'])}>
          <option value="draft">Entwurf – nicht öffentlich</option>
          <option value="active">Aktiv – Anmeldung möglich</option>
          <option value="archived">Archiviert</option>
        </select>
      </div>
      <SaveBar {...save} />
    </CardShell>
  );
}

function VideoCard({ webinar, onChanged }: { webinar: WebinarDetail; onChanged: () => void }) {
  const v = webinar.video;
  const processing = v !== null && (v.status === 'queued' || v.status === 'probing' || v.status === 'transcoding');

  // Während der Aufbereitung alle drei Sekunden nachsehen – der Fortschritt steht in der Datenbank.
  useEffect(() => {
    if (!processing) return;
    const timer = setInterval(onChanged, 3000);
    return () => clearInterval(timer);
  }, [processing, onChanged]);

  return (
    <CardShell id="webinar.video" title="Video">
      {!v && <p className="muted">Noch kein Video. Ohne Video zeigt der Raum einen Platzhalter mit laufender Uhr.</p>}
      {v && v.status === 'queued' && <p><strong>Upload angekommen</strong> · wartet auf die Aufbereitung</p>}
      {v && v.status === 'probing' && <p><strong>Dein Video wird geprüft …</strong></p>}
      {v && v.status === 'transcoding' && (
        <>
          <div className="progress" role="progressbar" aria-valuenow={v.progressPercent} aria-valuemin={0} aria-valuemax={100}>
            <div style={{ width: `${v.progressPercent}%` }} />
          </div>
          <p><strong>{uploadText('upload.processing', { prozent: v.progressPercent }).title}</strong></p>
          <p className="muted small">{uploadText('upload.processing').text}</p>
        </>
      )}
      {v && v.status === 'ready' && (
        <>
          <p><strong>{uploadText('upload.processing.done').title}</strong></p>
          <p className="muted small">
            {v.width} × {v.height} · {Math.round((v.durationSeconds ?? 0) / 60)} Minuten · {v.originalFilename}
          </p>
        </>
      )}
      {v && v.status === 'failed' && (
        <div className="error">
          <strong>{uploadText('upload.processing.failed').title}</strong>
          <div style={{ marginTop: 4 }}>{uploadText('upload.processing.failed').text}</div>
        </div>
      )}
      {v?.rejectionCode && (
        <div className="error">
          <strong>{uploadText(v.rejectionCode, { format: v.originalFilename.split('.').pop() ?? '' }).title}</strong>
          <div style={{ marginTop: 4 }}>{uploadText(v.rejectionCode).text}</div>
        </div>
      )}
      {v?.warnings.map((w) => (
        <div key={w} className="warning">
          <strong>{uploadText(w, { breite: v.width ?? 0, hoehe: v.height ?? 0 }).title}</strong>
          <div style={{ marginTop: 4 }}>{uploadText(w).text}</div>
        </div>
      ))}
      {v?.status === 'ready' && (webinar.beyondEnd.cta || webinar.beyondEnd.hints.length > 0) && (
        <div className="warning">
          <strong>Zeitpunkte hinter dem Ende des Videos:</strong>{' '}
          {webinar.beyondEnd.cta ? 'das Angebot ' : ''}
          {webinar.beyondEnd.cta && webinar.beyondEnd.hints.length > 0 ? 'und ' : ''}
          {webinar.beyondEnd.hints.length > 0 ? `${webinar.beyondEnd.hints.length} Hinweis(e) ` : ''}
          würden nie erscheinen. Bitte unten anpassen.
        </div>
      )}
      {!processing && <VideoUpload webinar={webinar} onChanged={onChanged} />}
    </CardShell>
  );
}

function WaitingImageCard({ webinar, onChanged }: { webinar: WebinarDetail; onChanged: () => void }) {
  return (
    <CardShell id="webinar.waiting" title="Wartezimmer">
      <p className="muted small">Das Bild hinter dem Countdown, bevor es losgeht. Ohne eigenes Bild nimmt der Raum das Vorschaubild aus dem Video.</p>
      <ImageUpload
        current={webinar.waitingImageUrl}
        alt="Wartezimmer-Bild"
        previewClass="image-upload__preview--wide"
        hint="JPG, PNG oder WebP, 16:9, gern 1920 × 1080, höchstens 5 MB."
        onUpload={async (file) => {
          await adminApi.uploadWaitingImage(webinar.id, file);
          onChanged();
        }}
        onRemove={async () => {
          await adminApi.removeWaitingImage(webinar.id);
          onChanged();
        }}
      />
    </CardShell>
  );
}

function SchedulesCard({ webinar, onSaved }: { webinar: WebinarDetail; onSaved: () => void }) {
  const [rules, setRules] = useState<ScheduleRule[]>(webinar.schedules.map(({ id: _id, ...rest }) => rest));
  const save = useSave(async () => {
    await adminApi.putSchedules(webinar.id, rules);
    onSaved();
  });

  const update = (index: number, rule: ScheduleRule) => setRules(rules.map((r, i) => (i === index ? rule : r)));
  const remove = (index: number) => setRules(rules.filter((_, i) => i !== index));

  return (
    <CardShell
      as="form"
      id="webinar.schedules"
      title="Termine"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run(undefined);
      }}
    >
      <p className="muted small">Feste Wochentage und Uhrzeiten, dazu wahlweise der nächste runde Zeitpunkt für Spontane. Zeitzone Europe/Berlin.</p>
      {rules.length === 0 && <p className="muted">Keine Termin-Regel – ohne Regel kann sich niemand anmelden.</p>}
      {rules.map((rule, index) => (
        <div key={index} className="rule">
          {rule.kind === 'recurring' ? (
            <>
              <div className="weekdays">
                {WEEKDAYS.map((d) => (
                  <label key={d.value} className={`weekday${rule.weekdays.includes(d.value) ? ' is-on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={rule.weekdays.includes(d.value)}
                      onChange={(e) => update(index, { ...rule, weekdays: e.target.checked ? [...rule.weekdays, d.value] : rule.weekdays.filter((v) => v !== d.value) })}
                    />
                    {d.label}
                  </label>
                ))}
              </div>
              <div className="field">
                <label>Uhrzeiten (mit Komma getrennt)</label>
                <input type="text" value={rule.times.join(', ')} placeholder="19:00, 20:30" onChange={(e) => update(index, { ...rule, times: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} />
              </div>
            </>
          ) : (
            <div className="field">
              <label>Nächster runder Zeitpunkt</label>
              <select value={rule.roundTo} onChange={(e) => update(index, { kind: 'next_round', roundTo: e.target.value as 'hour' | 'half_hour' })}>
                <option value="hour">zur nächsten vollen Stunde</option>
                <option value="half_hour">zur nächsten halben Stunde</option>
              </select>
            </div>
          )}
          <button type="button" className="link-button" onClick={() => remove(index)}>
            Regel entfernen
          </button>
        </div>
      ))}
      <div className="row-actions">
        <button type="button" className="button button--secondary button--small" onClick={() => setRules([...rules, { kind: 'recurring', weekdays: [1, 2, 3, 4, 5], times: ['19:00'] }])}>
          + Feste Termine
        </button>
        {!rules.some((r) => r.kind === 'next_round') && (
          <button type="button" className="button button--secondary button--small" onClick={() => setRules([...rules, { kind: 'next_round', roundTo: 'hour' }])}>
            + Nächster runder Zeitpunkt
          </button>
        )}
      </div>
      <SaveBar {...save} label="Termine speichern" />
    </CardShell>
  );
}

function HintsCard({ webinar, onSaved }: { webinar: WebinarDetail; onSaved: () => void }) {
  const [hints, setHints] = useState(webinar.hints.map((h) => ({ at: toClock(h.offsetSeconds), text: h.text })));
  const [beyond, setBeyond] = useState<number[]>([]);
  const save = useSave(async () => {
    const result = await adminApi.putHints(
      webinar.id,
      hints.filter((h) => h.text.trim()).map((h) => ({ offsetSeconds: fromClock(h.at), text: h.text.trim() })),
    );
    setBeyond(result.beyondEnd);
    onSaved();
  });

  return (
    <CardShell
      as="form"
      id="webinar.hints"
      title="Hinweise vom Team"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run(undefined);
      }}
    >
      <p className="muted small">Erscheinen zur angegebenen Minute im Raum – erkennbar vom Team, nicht als Mitteilnehmer.</p>
      {hints.map((h, index) => (
        <div key={index} className="hint-row">
          <input type="text" className="hint-row__time" value={h.at} placeholder="Min:Sek" onChange={(e) => setHints(hints.map((x, i) => (i === index ? { ...x, at: e.target.value } : x)))} />
          <input type="text" value={h.text} placeholder="Text" maxLength={1000} onChange={(e) => setHints(hints.map((x, i) => (i === index ? { ...x, text: e.target.value } : x)))} />
          <button type="button" className="link-button" onClick={() => setHints(hints.filter((_, i) => i !== index))} aria-label="Hinweis entfernen">
            ×
          </button>
        </div>
      ))}
      <div className="row-actions">
        <button type="button" className="button button--secondary button--small" onClick={() => setHints([...hints, { at: '0:30', text: '' }])}>
          + Hinweis
        </button>
      </div>
      {beyond.length > 0 && <div className="warning">Achtung: {beyond.length} Hinweis(e) liegen hinter dem Ende des Videos ({beyond.map(toClock).join(', ')}) und erscheinen nie.</div>}
      <SaveBar {...save} label="Hinweise speichern" />
    </CardShell>
  );
}

function OfferCard({ webinar, onSaved }: { webinar: WebinarDetail; onSaved: (w: WebinarDetail) => void }) {
  const [offset, setOffset] = useState(toMinutes(webinar.cta.offsetSeconds));
  const [headline, setHeadline] = useState(webinar.cta.headline ?? '');
  const [text, setText] = useState(webinar.cta.text ?? '');
  const [button, setButton] = useState(webinar.cta.buttonLabel ?? '');
  const [url, setUrl] = useState(webinar.cta.url ?? '');
  const [deadline, setDeadline] = useState(webinar.cta.deadlineHours === null ? '' : String(webinar.cta.deadlineHours));
  const save = useSave(async () =>
    onSaved(
      await adminApi.updateWebinar(webinar.id, {
        ctaOffsetSeconds: fromMinutes(offset),
        ctaHeadline: headline || null,
        ctaText: text || null,
        ctaButtonLabel: button || null,
        ctaUrl: url || null,
        ctaDeadlineHours: deadline.trim() === '' ? null : Math.max(1, Math.round(Number(deadline))),
      }),
    ),
  );
  const offsetSeconds = fromMinutes(offset);

  return (
    <CardShell
      as="form"
      id="webinar.offer"
      title="Angebot"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run(undefined);
      }}
    >
      <p className="muted small">Genau eine Einblendung ab der eingestellten Minute, bis zum Ende und auf der Schluss-Seite. Kein Dauerbutton.</p>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="o-offset">Erscheint ab Minute</label>
          <input id="o-offset" type="number" min={0} step={1} value={offset} placeholder="z. B. 50" onChange={(e) => setOffset(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="o-deadline">Frist in Stunden nach dem Webinar</label>
          <input id="o-deadline" type="number" min={1} step={1} value={deadline} placeholder="leer = keine Frist" onChange={(e) => setDeadline(e.target.value)} />
          <span className="faint small">Nur mit Frist gibt es einen Countdown – und dann muss sie gelten.</span>
        </div>
      </div>
      {offsetSeconds !== null && offsetSeconds >= webinar.durationSeconds && <div className="warning">Minute {offset} liegt hinter dem Ende ({Math.round(webinar.durationSeconds / 60)} Minuten) – das Angebot erschiene nie.</div>}
      <div className="field">
        <label htmlFor="o-headline">Überschrift</label>
        <input id="o-headline" type="text" value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={200} />
      </div>
      <div className="field">
        <label htmlFor="o-text">Text</label>
        <textarea id="o-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} />
      </div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="o-button">Beschriftung des Knopfs</label>
          <input id="o-button" type="text" value={button} onChange={(e) => setButton(e.target.value)} maxLength={80} placeholder="Angebot ansehen" />
        </div>
        <div className="field">
          <label htmlFor="o-url">Adresse der Buchungsseite</label>
          <input id="o-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
        </div>
      </div>
      <SaveBar {...save} label="Angebot speichern" />
    </CardShell>
  );
}

function AfterCard({ webinar, onSaved }: { webinar: WebinarDetail; onSaved: (w: WebinarDetail) => void }) {
  const [enabled, setEnabled] = useState(webinar.followup.enabled);
  const [delay, setDelay] = useState(String(webinar.followup.delayMinutes));
  const [replay, setReplay] = useState(webinar.replayValidHours === null ? '' : String(webinar.replayValidHours));
  const save = useSave(async () =>
    onSaved(
      await adminApi.updateWebinar(webinar.id, {
        followupEnabled: enabled,
        followupDelayMinutes: Math.min(10080, Math.max(0, Math.round(Number(delay) || 0))),
        replayValidHours: replay.trim() === '' ? null : Math.max(1, Math.round(Number(replay))),
      }),
    ),
  );

  return (
    <CardShell
      as="form"
      id="webinar.after"
      title="Nach dem Webinar"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run(undefined);
      }}
    >
      <p className="muted small">Die Nachfassmail unterscheidet Teilnehmer und No-Shows nach der Teilnehmer-Schwelle. Teilnehmer bekommen das Angebot, No-Shows die Aufzeichnung oder einen neuen Termin.</p>
      <label className="choice">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Nachfassmail senden</span>
      </label>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="a-delay">Minuten nach dem Ende</label>
          <input id="a-delay" type="number" min={0} max={10080} value={delay} disabled={!enabled} onChange={(e) => setDelay(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="a-replay">Aufzeichnung verfügbar für Stunden</label>
          <input id="a-replay" type="number" min={1} value={replay} placeholder="leer = keine Aufzeichnung" onChange={(e) => setReplay(e.target.value)} />
          <span className="faint small">Ab Ende des Termins, mit Bedienleiste, Link in der Nachfassmail.</span>
        </div>
      </div>
      <SaveBar {...save} label="Speichern" />
    </CardShell>
  );
}

function SettingsCard({ webinar, onSaved }: { webinar: WebinarDetail; onSaved: (w: WebinarDetail) => void }) {
  const [duration, setDuration] = useState(toMinutes(webinar.durationSeconds));
  const [threshold, setThreshold] = useState(String(webinar.attendanceThresholdPercent));
  const [cutoff, setCutoff] = useState(toMinutes(webinar.lateEntryCutoffSeconds));
  const [fullscreen, setFullscreen] = useState(webinar.fullscreenEnabled);
  const save = useSave(async () =>
    onSaved(
      await adminApi.updateWebinar(webinar.id, {
        ...(webinar.video ? {} : { durationSeconds: Math.max(60, (fromMinutes(duration) ?? 3600)) }),
        attendanceThresholdPercent: Math.min(100, Math.max(0, Math.round(Number(threshold) || 0))),
        lateEntryCutoffSeconds: fromMinutes(cutoff),
        fullscreenEnabled: fullscreen,
      }),
    ),
  );

  return (
    <CardShell
      as="form"
      id="webinar.settings"
      title="Einstellungen"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run(undefined);
      }}
    >
      <div className="field">
        <label htmlFor="s-duration">Dauer in Minuten</label>
        <input id="s-duration" type="number" min={1} value={duration} disabled={!!webinar.video} onChange={(e) => setDuration(e.target.value)} />
        {webinar.video && <span className="faint small">Kommt vom Video.</span>}
      </div>
      <div className="field">
        <label htmlFor="s-threshold">Ab wie viel Prozent der Laufzeit zählt jemand als Teilnehmer?</label>
        <input id="s-threshold" type="number" min={0} max={100} value={threshold} onChange={(e) => setThreshold(e.target.value)} />
        <span className="faint small">Darunter gilt jemand als No-Show – wichtig für die Nachfassmails.</span>
      </div>
      <div className="field">
        <label htmlFor="s-cutoff">Einlassgrenze in Minuten nach Start</label>
        <input id="s-cutoff" type="number" min={0} value={cutoff} placeholder="leer = später einsteigen immer erlaubt" onChange={(e) => setCutoff(e.target.value)} />
      </div>
      <label className="choice">
        <input type="checkbox" checked={fullscreen} onChange={(e) => setFullscreen(e.target.checked)} />
        <span>
          Vollbild im Raum erlauben <span className="muted small">– das Angebot erscheint im Vollbild als Banner über dem Video.</span>
        </span>
      </label>
      <SaveBar {...save} label="Einstellungen speichern" />
    </CardShell>
  );
}

function RegistrationsCard({ id, count }: { id: string; count: number }) {
  const [rows, setRows] = useState<Registration[] | null>(null);
  const [open, toggle] = useCollapsed('webinar.registrations', false);
  useEffect(() => {
    if (open && rows === null) {
      adminApi
        .listRegistrations(id)
        .then((r) => setRows(r.registrations))
        .catch(() => setRows([]));
    }
  }, [open, rows, id]);

  return (
    <CardShell
      id="webinar.registrations"
      style={{ marginTop: 24 }}
      title={`Anmeldungen (${count})`}
      open={open}
      onToggle={toggle}
      right={
        count > 0 ? (
          <a className="button button--secondary button--small" href={adminApi.registrationsCsvUrl(id)} download>
            Als CSV herunterladen
          </a>
        ) : undefined
      }
    >
      {rows === null && <p className="muted">Wird geladen …</p>}
      {rows?.length === 0 && <p className="muted">Noch keine Anmeldungen.</p>}
      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Termin</th>
              <th>Angemeldet</th>
              <th>Gesehen</th>
              <th>Status</th>
              <th>Angebot</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={r.cancelledAt ? 'is-muted' : ''}>
                <td>{r.name ?? '–'}</td>
                <td>{r.email}</td>
                <td>{formatSession(r.sessionStartsAt, 'Europe/Berlin')}</td>
                <td>{new Date(r.createdAt).toLocaleDateString('de-DE')}</td>
                <td>{r.watchedSeconds > 0 ? `${Math.round(r.watchedSeconds / 60)} Min.` : '–'}</td>
                <td>{r.cancelledAt ? 'abgesagt' : r.attended === null ? <span className="faint">bevorstehend</span> : r.attended ? 'teilgenommen' : <span className="muted">nicht erschienen</span>}</td>
                <td>{r.ctaClicked ? 'geklickt' : <span className="faint">–</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardShell>
  );
}

function QuestionsCard({ id }: { id: string }) {
  const [rows, setRows] = useState<Question[] | null>(null);
  const [showAnswered, setShowAnswered] = useState(false);
  const load = useCallback(() => {
    adminApi
      .listQuestions(id)
      .then((r) => setRows(r.questions))
      .catch(() => setRows([]));
  }, [id]);
  useEffect(load, [load]);

  // Solange eine Antwort-Mail in der Warteschlange steht, alle 15 Sekunden nachsehen, ob sie raus ist.
  const pending = rows?.some((q) => q.mailStatus === 'queued') ?? false;
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(load, 15_000);
    return () => clearInterval(timer);
  }, [pending, load]);

  const open = rows?.filter((q) => q.answeredAt === null) ?? [];
  const answered = rows?.filter((q) => q.answeredAt !== null) ?? [];
  const replace = (q: Question) => setRows((rows ?? []).map((r) => (r.id === q.id ? q : r)));

  return (
    <CardShell
      id="webinar.questions"
      style={{ marginTop: 24 }}
      title={`Fragen aus dem Raum${rows ? ` (${open.length} offen)` : ''}`}
      right={
        answered.length > 0 ? (
          <button type="button" className="link-button" onClick={() => setShowAnswered(!showAnswered)}>
            {showAnswered ? 'Beantwortete ausblenden' : `${answered.length} beantwortete anzeigen`}
          </button>
        ) : undefined
      }
    >
      <p className="muted small">Die Antwort geht per E-Mail an die Person, die gefragt hat – mit ihrer Frage im Wortlaut.</p>
      {rows === null && <p className="muted">Wird geladen …</p>}
      {rows?.length === 0 && <p className="muted">Noch keine Fragen.</p>}
      {open.map((q) => (
        <QuestionRow key={q.id} question={q} onAnswered={replace} />
      ))}
      {showAnswered && answered.map((q) => <QuestionRow key={q.id} question={q} onAnswered={replace} />)}
    </CardShell>
  );
}

function QuestionRow({ question: q, onAnswered }: { question: Question; onAnswered: (q: Question) => void }) {
  const [answer, setAnswer] = useState('');
  const save = useSave(async () => onAnswered(await adminApi.answerQuestion(q.id, answer.trim())));
  const minute = Math.floor(q.positionSeconds / 60);

  return (
    <div className="question">
      <div className="question__meta small muted">
        <span>
          <strong>{q.name ?? q.email}</strong>
          {q.name ? ` · ${q.email}` : ''}
        </span>
        <span>Termin {formatSession(q.sessionStartsAt, 'Europe/Berlin')}</span>
        <span>{minute > 0 ? `bei Minute ${minute}` : 'vor dem Start'}</span>
        <span>gefragt am {new Date(q.askedAt).toLocaleDateString('de-DE')}</span>
      </div>
      <p className="question__text">{q.text}</p>
      {q.answeredAt !== null ? (
        <>
          <p className="question__answer">{q.answer}</p>
          <p className="faint small" style={{ marginTop: 6 }}>
            Beantwortet am {new Date(q.answeredAt).toLocaleDateString('de-DE')}
            {q.mailStatus === 'sent' ? ' · Mail zugestellt' : q.mailStatus === 'failed' ? ' · Mail konnte nicht gesendet werden' : q.mailStatus === 'queued' ? ' · Mail wird gesendet' : ''}
          </p>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (answer.trim()) void save.run(undefined);
          }}
        >
          <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} maxLength={5000} placeholder="Deine Antwort …" />
          <SaveBar {...save} label="Antwort senden" />
        </form>
      )}
    </div>
  );
}

function StatsCard({ id }: { id: string }) {
  const [stats, setStats] = useState<WebinarStats | null>(null);
  useEffect(() => {
    adminApi.getStats(id).then(setStats).catch(() => setStats(null));
  }, [id]);
  if (!stats) return null;

  const s = stats;
  const nothingYet = s.registrations.total + s.registrations.cancelled === 0;
  return (
    <CardShell id="webinar.stats" className="stats" title="Zahlen" right={<span className="muted small">Teilnehmer ab {s.attendanceThresholdPercent} % der Laufzeit</span>}>
      {nothingYet && <p className="muted">Noch keine Anmeldungen – sobald sich jemand anmeldet, stehen hier Show-Rate, Sehdauer und Angebots-Klicks.</p>}
      {!nothingYet && (
        <>
          <div className="stats__row">
            <Stat label="Anmeldungen" value={String(s.registrations.total)} note={`${s.registrations.upcoming} bevorstehend · ${s.registrations.cancelled} abgesagt`} />
            <Stat label="Show-Rate" value={s.showRatePercent === null ? '–' : `${s.showRatePercent} %`} note={`${s.attended} teilgenommen · ${s.noShows} nicht erschienen`} />
            <Stat label="Sehdauer im Schnitt" value={s.averageWatchedSeconds === null ? '–' : `${Math.round(s.averageWatchedSeconds / 60)} Min.`} note={`von ${Math.round(s.durationSeconds / 60)} Minuten`} />
            <Stat label="Angebot geklickt" value={String(s.cta.registrations)} note={s.cta.clicks !== s.cta.registrations ? `${s.cta.clicks} Klicks` : s.attended > 0 ? `${Math.round((s.cta.registrations / s.attended) * 100)} % der Teilnehmer` : ''} />
            <Stat label="Fragen" value={String(s.questions.total)} note={s.questions.open > 0 ? `${s.questions.open} offen` : ''} />
          </div>
          <WatchCurve stats={s} />
        </>
      )}
    </CardShell>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
      {note && <div className="faint small">{note}</div>}
    </div>
  );
}

/** Watch-Time-Kurve: je Videominute die Zahl der Zuschauer, das Angebot als Markierung. */
function WatchCurve({ stats: s }: { stats: WebinarStats }) {
  const minutes = s.watchCurve.length;
  const peak = Math.max(1, ...s.watchCurve);
  if (!s.watchCurve.some((v) => v > 0)) return <p className="muted small">Die Sehdauer-Kurve erscheint, sobald jemand zugeschaut hat.</p>;

  const W = 600;
  const H = 140;
  const pad = { l: 28, r: 8, t: 10, b: 22 };
  const x = (minute: number) => pad.l + ((W - pad.l - pad.r) * minute) / minutes;
  const y = (viewers: number) => pad.t + (H - pad.t - pad.b) * (1 - viewers / peak);
  const points = s.watchCurve.map((v, m) => `${x(m + 0.5).toFixed(1)},${y(v).toFixed(1)}`);
  const area = `M${x(0.5).toFixed(1)},${y(0).toFixed(1)} L${points.join(' L')} L${x(minutes - 0.5).toFixed(1)},${y(0).toFixed(1)} Z`;
  const ctaMinute = s.cta.offsetSeconds === null ? null : s.cta.offsetSeconds / 60;
  const ticks = [0, Math.round(minutes / 2), minutes];

  return (
    <figure className="curve">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Zuschauer je Minute, Spitze ${peak}`}>
        <line x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} className="curve__axis" />
        <text x={pad.l - 6} y={y(peak) + 4} className="curve__tick" textAnchor="end">{peak}</text>
        <text x={pad.l - 6} y={y(0) + 4} className="curve__tick" textAnchor="end">0</text>
        {ticks.map((m) => (
          <text key={m} x={x(m)} y={H - 6} className="curve__tick" textAnchor={m === 0 ? 'start' : m === minutes ? 'end' : 'middle'}>{m} Min.</text>
        ))}
        <path d={area} className="curve__area" />
        <polyline points={points.join(' ')} className="curve__line" />
        {ctaMinute !== null && ctaMinute < minutes && (
          <>
            <line x1={x(ctaMinute)} x2={x(ctaMinute)} y1={pad.t} y2={y(0)} className="curve__cta" />
            <text x={x(ctaMinute) + 4} y={pad.t + 10} className="curve__tick">Angebot</text>
          </>
        )}
        {s.cta.byMinute.map((c) => (
          <circle key={c.minute} cx={x(c.minute + 0.5)} cy={y(0)} r={3 + Math.min(4, c.clicks)} className="curve__click">
            <title>{c.clicks} Klick(s) bei Minute {c.minute}</title>
          </circle>
        ))}
      </svg>
      <figcaption className="faint small">Zuschauer je Videominute über alle Termine · Punkte: Klicks auf das Angebot</figcaption>
    </figure>
  );
}
